/** Period arithmetic and labels for NxPeriodNavigator. Pure, locale via Intl. */

export type NxPeriodUnit = 'day' | 'week' | 'month' | 'quarter' | 'year';

/** Start of the period that contains `date` (weeks start on Monday). */
export function startOfPeriod(date: Date, unit: NxPeriodUnit): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (unit === 'week') {
    const shift = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - shift);
  } else if (unit === 'month') {
    d.setDate(1);
  } else if (unit === 'quarter') {
    d.setMonth(Math.floor(d.getMonth() / 3) * 3, 1);
  } else if (unit === 'year') {
    d.setMonth(0, 1);
  }
  return d;
}

/** `date` moved by `steps` periods (month ends clamp: 31 Jan + 1 month = 28/29 Feb). */
export function shiftPeriod(date: Date, unit: NxPeriodUnit, steps: number): Date {
  const d = new Date(date);
  if (unit === 'day') d.setDate(d.getDate() + steps);
  else if (unit === 'week') d.setDate(d.getDate() + steps * 7);
  else {
    const months = unit === 'month' ? steps : unit === 'quarter' ? steps * 3 : steps * 12;
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
  }
  return d;
}

/** Whether `a` and `b` fall into the same period. */
export function samePeriod(a: Date, b: Date, unit: NxPeriodUnit): boolean {
  return startOfPeriod(a, unit).getTime() === startOfPeriod(b, unit).getTime();
}

/** ISO 8601 week number (weeks start Monday, week 1 holds the first Thursday). */
export function isoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/**
 * Label of the period that contains `date`: „Mi., 23. Sept. 2026“,
 * „KW 39 · 21.–27. Sept. 2026“, „September 2026“, „Q3 2026“, „2026“.
 * `weekPrefix` is the calendar-week abbreviation („KW“).
 */
export function periodLabel(
  date: Date,
  unit: NxPeriodUnit,
  locale?: string,
  weekPrefix = 'KW'
): string {
  if (unit === 'day') {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }
  if (unit === 'week') {
    const start = startOfPeriod(date, 'week');
    const end = shiftPeriod(start, 'day', 6);
    const range = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).formatRange(start, end);
    return `${weekPrefix} ${isoWeek(date)} · ${range}`;
  }
  if (unit === 'month') {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
  }
  if (unit === 'quarter') {
    return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
  }
  return String(date.getFullYear());
}
